import { useCallback, useEffect, useState } from 'react';

import { useNavigate } from 'react-router-dom';

import { Button, ShimmerDiv, showSnackbar } from 'fluxo-ui';
import { BarChartIcon, BuildingIcon, CalendarIcon, QuestionCircle, UserGroupIcon, WarningIcon } from 'fluxo-ui/icons';

import { Card, EmptyState, PageHeader, StatCard } from '../common';
import analyticsService from '../../services/analytics.service';
import { formatDateTime } from '../../utils/formatting';

interface RecentOrganization {
    id: string;
    name: string;
    domain?: string;
    status?: string;
    createdAt: string;
}

interface SystemActivityItem {
    userId?: { profile?: { name?: string }; email?: string };
    examId?: { title?: string };
    percentage?: number;
    createdAt: string;
}

interface SystemStats {
    totalOrganizations: number;
    totalUsers: number;
    totalExams: number;
    totalQuestions: number;
    activeExams: number;
    pendingApprovals: number;
    recentOrganizations: RecentOrganization[];
    systemActivity: SystemActivityItem[];
}

const SuperAdminDashboard: React.FC = () => {
    const navigate = useNavigate();
    const [stats, setStats] = useState<SystemStats | null>(null);
    const [loading, setLoading] = useState(true);

    const fetchStats = useCallback(async () => {
        try {
            setLoading(true);
            const data = await analyticsService.getSystemStats();
            setStats(data);
        } catch (error: any) {
            showSnackbar(error.message || 'Failed to fetch system stats', 'Error', { type: 'error' });
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchStats();
    }, [fetchStats]);

    if (loading) {
        return (
            <div className="space-y-8">
                <ShimmerDiv level={2} className="h-20 w-full" />
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {[...Array(4)].map((_, i) => (
                        <ShimmerDiv key={i} level={3} className="h-32" />
                    ))}
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <PageHeader title="System Dashboard" description="Platform-wide metrics and management" className="mb-0" />

            {(stats?.pendingApprovals ?? 0) > 0 && (
                <div className="flex flex-wrap items-center justify-between gap-3 p-4 rounded-xl border bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800">
                    <div className="flex items-center gap-3">
                        <WarningIcon className="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0" />
                        <span className="text-sm font-medium text-slate-900 dark:text-white">
                            {stats?.pendingApprovals} questions pending approval
                        </span>
                    </div>
                    <Button label="Review" variant="default" size="sm" layout="outlined" onClick={() => navigate('/questions/pending')} />
                </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
                <StatCard
                    label="Organizations"
                    value={stats?.totalOrganizations ?? 0}
                    icon={BuildingIcon}
                    tone="blue"
                    onClick={() => navigate('/organizations')}
                />
                <StatCard
                    label="Total Users"
                    value={stats?.totalUsers ?? 0}
                    icon={UserGroupIcon}
                    tone="emerald"
                    onClick={() => navigate('/users')}
                />
                <StatCard
                    label="All Exams"
                    value={stats?.totalExams ?? 0}
                    icon={CalendarIcon}
                    tone="violet"
                    hint={`${stats?.activeExams ?? 0} active`}
                    onClick={() => navigate('/exams')}
                />
                <StatCard
                    label="Question Bank"
                    value={stats?.totalQuestions ?? 0}
                    icon={QuestionCircle}
                    tone="amber"
                    hint={`${stats?.pendingApprovals ?? 0} pending approval`}
                    onClick={() => navigate('/questions')}
                />
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
                <Card
                    title="Recent Organizations"
                    actions={
                        <Button label="View All" variant="default" size="sm" layout="outlined" onClick={() => navigate('/organizations')} />
                    }
                    padding="none"
                >
                    {stats?.recentOrganizations && stats.recentOrganizations.length > 0 ? (
                        <div className="divide-y divide-slate-100 dark:divide-slate-800">
                            {stats.recentOrganizations.map((org) => (
                                <button
                                    key={org.id}
                                    className="w-full flex items-center gap-4 px-4 sm:px-5 py-3.5 text-left hover:bg-slate-50 dark:hover:bg-slate-800/60 transition-colors"
                                    onClick={() => navigate(`/organizations/${org.id}`)}
                                >
                                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-teal-600 to-cyan-600 text-white flex items-center justify-center font-semibold shrink-0">
                                        {(org.name || '?').charAt(0).toUpperCase()}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <h3 className="font-medium text-sm text-slate-900 dark:text-white truncate">{org.name}</h3>
                                        <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{org.domain || 'No domain'}</p>
                                    </div>
                                    <div className="text-xs text-slate-500 dark:text-slate-400 shrink-0 tabular-nums">
                                        {formatDateTime(org.createdAt)}
                                    </div>
                                </button>
                            ))}
                        </div>
                    ) : (
                        <EmptyState
                            icon={BuildingIcon}
                            title="No recent organizations"
                            description="Newly created organizations will appear here"
                            size="sm"
                        />
                    )}
                </Card>

                <Card title="Recent Activity" padding="none">
                    {stats?.systemActivity && stats.systemActivity.length > 0 ? (
                        <div className="divide-y divide-slate-100 dark:divide-slate-800">
                            {stats.systemActivity.map((activity, index) => (
                                <div key={index} className="flex items-start gap-3 px-4 sm:px-5 py-3.5">
                                    <span className="w-2 h-2 rounded-full bg-emerald-500 mt-1.5 shrink-0" aria-hidden />
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm text-slate-900 dark:text-white">
                                            <span className="font-medium">
                                                {activity.userId?.profile?.name || activity.userId?.email || 'Unknown User'}
                                            </span>{' '}
                                            completed <span className="font-medium">{activity.examId?.title || 'Unknown Exam'}</span>
                                        </p>
                                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 tabular-nums">
                                            Score: {activity.percentage?.toFixed(1)}% • {formatDateTime(activity.createdAt)}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <EmptyState
                            icon={BarChartIcon}
                            title="No recent activity"
                            description="Completed exam attempts will appear here"
                            size="sm"
                        />
                    )}
                </Card>
            </div>
        </div>
    );
};

export default SuperAdminDashboard;
